import { nextTick } from 'vue';
import { createI18n, I18n } from 'vue-i18n';
import {api, orgApi} from "@/utils/Api.util";
import { Settings as LuxonSettings } from "luxon";
import type { Locale } from 'date-fns';


// Locales for which we have translation files
export const SUPPORTED_LOCALES: {[ key: string] : string } = {
	'en-GB': 'English (UK)',
	'nl-BE': 'Nederlands (BE)',
	'nl-NL': 'Nederlands (NL)',
	'es-ES': 'Español (ES)',
	'fr-FR': 'Français (FR)',
	'de-DE': 'Deutsch (DE)',
	'bg-BG': 'Български (BG)',
};

export const DEFAULT_LOCALE = 'en-GB';

// Locales for which we have translation files, but may not be complete (and thus should fallback to another locale)
const FALLBACK_LOCALES = {
	'nl-NL': 'nl-BE',
}

// Locales that are aliased to another locale (e.g. locale 'nl' provided by browser is aliased to 'nl-BE')
const ALIAS_LOCALES: { [ key: string] : string } = {
	'nl': 'nl-BE',
	'en': 'en-GB',
	'es': 'es-ES',
	'fr': 'fr-FR',
	'fr-BE': 'fr-FR',
	'de': 'de-DE',
	'bg': 'bg-BG',
}

// Some external components use date-fns for date formatting, which requires a date-fns locale object.
// Because dynamic paths can't be resolved at runtime, we provide a function to dynamically load the locale that corresponds with the vue-i18n locale string.
// SHOULD BE KEPT IN SYNC WITH SUPPORTED_LOCALES.
const DATE_FNS_LOCALE_LOADERS: { [ key: string ]: () => Promise<any> } = {
	'en-GB': () => import('date-fns/locale/en-GB/index.js'),
	'nl-BE': () => import('date-fns/locale/nl-BE/index.js'),
	'nl-NL': () => import('date-fns/locale/nl/index.js'),
	'es-ES': () => import('date-fns/locale/es/index.js'),
	'fr-FR': () => import('date-fns/locale/fr/index.js'),
	'de-DE': () => import('date-fns/locale/de/index.js'),
	'bg-BG': () => import('date-fns/locale/bg/index.js'),
};

// Countries that can be selected
// Warning: we use the 2-letter ISO country code as key, not the 3-letter code.
// 3-letter code will not be accepted by the API.
export const SUPPORTED_COUNTRIES: {[ key: string] : string } = {
	'AT': 'Austria',
	'BE': 'Belgium',
	'BG': 'Bulgaria',
	'HR': 'Croatia',
	'CY': 'Cyprus',
	'CZ': 'Czechia',
	'DK': 'Denmark',
	'EE': 'Estonia',
	'FI': 'Finland',
	'FR': 'France',
	'DE': 'Germany',
	'GR': 'Greece',
	'HU': 'Hungary',
	'IE': 'Ireland',
	'IT': 'Italy',
	'LV': 'Latvia',
	'LT': 'Lithuania',
	'LU': 'Luxembourg',
	'MT': 'Malta',
	'NL': 'Netherlands',
	'PL': 'Poland',
	'PT': 'Portugal',
	'RO': 'Romania',
	'SK': 'Slovakia',
	'SI': 'Slovenia',
	'ES': 'Spain',
	'SE': 'Sweden',
	'UK': 'United Kingdom',
};

export let i18n: I18n;

export async function setupI18n(options: any = {}) {

	let localeOption = options.locale;
	delete options.locale;	// we don't want to pass locale to createI18n as it would add the locale to the list of available locales
							// without downloading the corresponding messages. We'll use a dedicated function to set/download locale.

	// disable warnings
	options.silentTranslationWarn = true;
	options.silentFallbackWarn = true;

	// A key without a translation is shown as-is; treat it as a message so its {parameters}
	// and plural forms still resolve instead of leaking into the UI.
	options.formatFallbackMessages = true;

	i18n = createI18n(options);
	await setI18nLanguage(i18n, localeOption);

	return i18n;
}

export async function setI18nLanguage(i18n: I18n, locale: string) {

	locale = resolveLocale(locale);

	// (down)load locale messages if necessary
    if (!i18n.global.availableLocales.includes(locale)) {
		await downloadMessages(i18n, locale);
	}

	if (i18n.mode === 'legacy') {
		i18n.global.locale = locale;
	} else {
		i18n.global.locale.value = locale;
	}

	api.defaults.headers.common['X-Locale'] = locale;
	orgApi.defaults.headers.common['X-Locale'] = locale;

	document.querySelector('html').setAttribute('lang', locale);

	LuxonSettings.defaultLocale = locale;

	if(FALLBACK_LOCALES[locale]) {
		await downloadMessages(i18n, FALLBACK_LOCALES[locale]);
		if (i18n.mode === 'legacy') {
			i18n.global.fallbackLocale = FALLBACK_LOCALES[locale];
		} else {
			i18n.global.fallbackLocale.value = FALLBACK_LOCALES[locale];
		}
	}
}

export async function downloadMessages(i18n, locale) {

	if(!validLocale(locale)) {
		throw new Error(`Invalid locale ${locale}`);
	}

	// load locale messages with dynamic import
	// Limitations: https://github.com/rollup/plugins/tree/master/packages/dynamic-import-vars#limitations
	const messages = await import(
		`./i18n/lang_${locale}.json`
	);

	// set locale and locale message
	i18n.global.setLocaleMessage(locale, messages.default);

	return nextTick();
}

function resolveLocale(locale) {

	let resolvedLocale = locale;
	if(ALIAS_LOCALES[locale]) {
		resolvedLocale = ALIAS_LOCALES[locale];
	}

	if(!validLocale(resolvedLocale)) {
		resolvedLocale = DEFAULT_LOCALE;
	}
	return resolvedLocale;
}

function validLocale(locale) {
	if(SUPPORTED_LOCALES[locale]) {
		return true;
	}
	return false;
}

export async function getDateFnsLocale(locale: string) {
	locale = resolveLocale(locale);
	const loader = DATE_FNS_LOCALE_LOADERS[locale] ?? DATE_FNS_LOCALE_LOADERS[DEFAULT_LOCALE];
	const module = await loader();
	return module.default as Locale;
}